import storage from './storage';
import logException from '@dashboard/services/logException';
import { apiUrl } from '@dashboard/services/env';
import { clearSession } from '@dashboard/services/clearSession';
import { loginUrl } from '@dashboard/components/AuthForm/authUrl';

export const NETWORK_ERROR_MESSAGE = 'Please check your internet connection and try again.';

function isNetworkFetchError(error) {
  const message = error?.message || '';

  return (
    error instanceof TypeError &&
    (message.startsWith('Failed to fetch') ||
      message.includes('NetworkError') ||
      message.includes('Load failed'))
  );
}

let isPageUnloading = false;
let unloadResetTimer;

// A request cancelled by leaving the page fails like a dropped connection.
// Firefox and webkit cancel it at beforeunload, chromium at pagehide.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    isPageUnloading = true;
    clearTimeout(unloadResetTimer);
    unloadResetTimer = setTimeout(() => {
      isPageUnloading = false;
    }, 1000);
  });

  window.addEventListener('pagehide', () => {
    clearTimeout(unloadResetTimer);
    isPageUnloading = true;
  });

  window.addEventListener('pageshow', () => {
    isPageUnloading = false;
  });
}

function getHost(url) {
  try {
    return new URL(url, window.location.href).host;
  } catch {
    return undefined;
  }
}

// A dropped connection surfaces as a bare TypeError whose wording differs per
// browser and leaks the API host into the UI. Swap it for copy a seller can act
// on, but report the original first so Sentry keeps the real signal.
export async function fetchWithNetworkErrorHandling(url, options = {}) {
  try {
    return await fetch(url, options);
  } catch (error) {
    if (!isNetworkFetchError(error)) {
      throw error;
    }

    if (!isPageUnloading) {
      const method = options.method || 'GET';
      const networkError = new Error(`Network error: ${error.message} - ${method} ${url}`);
      networkError.name = 'NetworkFetchError';
      networkError.originalError = error;
      networkError.url = url;
      networkError.method = method;
      networkError.host = getHost(url);
      logException(networkError);
    }

    error.message = NETWORK_ERROR_MESSAGE;
    error.sentryHandled = true;
    throw error;
  }
}

async function parseJSON(response) {
  if (!response.ok && !response.body) {
    return null;
  }

  if (response.status === 204 || response.status === 205) {
    return true;
  }

  const json = await response.json();
  json.meta = response;

  return json;
}

export async function checkAuth(response) {
  if (response.status === 401) {
    // Hard auth failure - remove session and redirect to login
    clearSession();

    window.location.href = loginUrl(window.location.pathname, window.location.search);

    // Return a promise that never resolves to prevent further processing
    return new Promise(() => {});
  }

  if (response.status === 403) {
    try {
      const body = await response.clone().json();

      if (body.code === 'ALL_STORES_DISABLED') {
        // Clear session and redirect to login
        clearSession();
        window.location.href = '/login';
        // Return a promise that never resolves to prevent further processing
        return new Promise(() => {});
      }

      if (body.code === 'NO_STORES_AVAILABLE') {
        window.location.href = '/dashboard/account/profile';
        return new Promise(() => {});
      }
    } catch {}
  }

  if (response.status === 409) {
    try {
      const body = await response.clone().json();

      if (
        (body.code === 'SELLER_ACCESS_REVOKED' || body.code === 'SELLER_DISABLED') &&
        body.currentSeller
      ) {
        // Update localStorage BEFORE redirect to prevent double reload
        storage.local.setItem('sellerId', body.currentSeller);
        storage.local.setItem('userSellerId', body.currentSeller);

        window.location.href = '/dashboard';
        // Return a promise that never resolves to prevent further processing
        return new Promise(() => {});
      }
    } catch {}
  }

  return response;
}

export default function request(url, token, options = {}) {
  const sellerId = storage.local.getItem('sellerId');

  return fetchWithNetworkErrorHandling(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'x-access-token': token }),
      ...(sellerId && { 'X-Current-Seller-Id': sellerId }),
      ...(options.headers && options.headers),
    },
  })
    .then(checkAuth)
    .then(parseJSON);
}

export async function fetcher(url, token, options = {}) {
  const sellerId = storage.local.getItem('sellerId');

  return fetchWithNetworkErrorHandling(apiUrl + url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'x-access-token': token }),
      ...(sellerId && { 'X-Current-Seller-Id': sellerId }),
      ...(options.headers && options.headers),
    },
  })
    .then(checkAuth)
    .then(parseJSON)
    .then(res => {
      if (res === true) return true;
      if (res === null) return null;
      if (res.data) return res.data;

      throw res || res.message;
    });
}

/* 
  use with useInfiniteQuery (react-query)
*/
export async function fetchInfinite(url, token, options = {}) {
  const sellerId = storage.local.getItem('sellerId');

  return fetchWithNetworkErrorHandling(apiUrl + url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'x-access-token': token }),
      ...(sellerId && { 'X-Current-Seller-Id': sellerId }),
      ...(options.headers && options.headers),
    },
  })
    .then(checkAuth)
    .then(parseJSON)
    .then(res => {
      if (res.data) return res;

      throw new Error(res.error || res.message);
    })
    .catch(error => {
      throw new Error(error?.error || error?.message);
    });
}
